import { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

const CHART_TYPES = [
  { value: 'bar', label: 'Bar Chart' },
  { value: 'stackedBar', label: 'Stacked Bar Chart' },
  { value: 'pie', label: 'Pie Chart' },
  { value: 'pivot', label: 'Pivot Table' },
  { value: 'advancedPivot', label: 'Advanced Pivot Table' }
];

const AGGREGATION_TYPES = [
  { value: 'sum', label: 'Sum' },
  { value: 'average', label: 'Average' },
  { value: 'count', label: 'Count' },
  { value: 'min', label: 'Minimum' },
  { value: 'max', label: 'Maximum' }
];

const AVAILABLE_FIELDS = [
  { value: 'vessel_name', label: 'Vessel', type: 'category' },
  { value: 'voyage_number', label: 'Voyage Number', type: 'category' },
  { value: 'port_name', label: 'Port/Position', type: 'category' },
  { value: 'actual_port_days', label: 'Actual Port Days', type: 'numeric' },
  { value: 'planned_days', label: 'Planned Port Days', type: 'numeric' },
  { value: 'laycan_deviation_days', label: 'Laycan Deviation Days', type: 'numeric' },
  { value: 'port_stay_deviation', label: 'Port Stay Deviation Days', type: 'numeric' },
  { value: 'laycan_status', label: 'Laycan Status', type: 'category' }
];

export default function ChartConfigDialog({ isOpen, onClose, onSave, editingWidget }) {
  const [chartType, setChartType] = useState('bar');
  const [title, setTitle] = useState('');
  const [categoryField, setCategoryField] = useState('');
  const [valueField, setValueField] = useState('');
  const [stackField, setStackField] = useState('');
  const [aggregationType, setAggregationType] = useState('sum');
  const [showTotals, setShowTotals] = useState(true);
  const [showLegend, setShowLegend] = useState(true);

  useEffect(() => {
    if (editingWidget) {
      setChartType(editingWidget.type);
      setTitle(editingWidget.title);
      setCategoryField(editingWidget.config.categoryField || '');
      setValueField(editingWidget.config.valueField || '');
      setStackField(editingWidget.config.stackField || '');
      setAggregationType(editingWidget.config.aggregationType || 'sum');
      setShowTotals(editingWidget.config.showTotals !== undefined ? editingWidget.config.showTotals : true);
      setShowLegend(editingWidget.config.showLegend !== undefined ? editingWidget.config.showLegend : true);
    } else {
      setChartType('bar');
      setTitle('');
      setCategoryField('');
      setValueField('');
      setStackField('');
      setAggregationType('sum');
      setShowTotals(true);
      setShowLegend(true);
    }
  }, [editingWidget, isOpen]);

  const handleSave = () => {
    if (!title || !categoryField) {
      alert('Please fill in all required fields');
      return;
    }

    if ((chartType === 'bar' || chartType === 'stackedBar') && !valueField) {
      alert('Please select a value field');
      return;
    }

    const config = {
      categoryField,
      valueField: valueField || categoryField,
      stackField: chartType === 'stackedBar' ? stackField : undefined,
      aggregationType: chartType === 'pivot' ? aggregationType : 'sum',
      showTotals: chartType === 'pivot' ? showTotals : undefined,
      showLegend
    };

    onSave({
      type: chartType,
      title,
      config
    });
  };

  if (!isOpen) return null;

  const categoryFields = AVAILABLE_FIELDS.filter(f => f.type === 'category');
  const numericFields = AVAILABLE_FIELDS.filter(f => f.type === 'numeric');

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>{editingWidget ? 'Edit Chart' : 'Add New Chart'}</CardTitle>
            <Button variant="ghost" size="sm" onClick={onClose}>
              <X className="w-4 h-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Chart Type */}
          <div>
            <label className="block text-sm font-medium mb-2">
              Chart Type <span className="text-red-500">*</span>
            </label>
            <select
              value={chartType}
              onChange={(e) => setChartType(e.target.value)}
              className="w-full border rounded px-3 py-2"
            >
              {CHART_TYPES.map(type => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>

          {/* Title */}
          <div>
            <label className="block text-sm font-medium mb-2">
              Chart Title <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g., Port Calls by Vessel"
              className="w-full border rounded px-3 py-2"
            />
          </div>

          {/* Category Field */}
          <div>
            <label className="block text-sm font-medium mb-2">
              Category Field <span className="text-red-500">*</span>
            </label>
            <select
              value={categoryField}
              onChange={(e) => setCategoryField(e.target.value)}
              className="w-full border rounded px-3 py-2"
            >
              <option value="">Select a field...</option>
              {AVAILABLE_FIELDS.map(field => (
                <option key={field.value} value={field.value}>
                  {field.label}
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-1">
              The field to group data by (X-axis for bar charts, labels for pie charts)
            </p>
          </div>

          {/* Value Field (for bar charts) */}
          {(chartType === 'bar' || chartType === 'stackedBar') && (
            <div>
              <label className="block text-sm font-medium mb-2">
                Value Field <span className="text-red-500">*</span>
              </label>
              <select
                value={valueField}
                onChange={(e) => setValueField(e.target.value)}
                className="w-full border rounded px-3 py-2"
              >
                <option value="">Select a field...</option>
                <option value="_count">Count (Number of Records)</option>
                {numericFields.map(field => (
                  <option key={field.value} value={field.value}>
                    {field.label} (Sum)
                  </option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-1">
                The field to measure (Y-axis for bar charts)
              </p>
            </div>
          )}

          {/* Stack Field (for stacked bar charts) */}
          {chartType === 'stackedBar' && (
            <div>
              <label className="block text-sm font-medium mb-2">
                Stack Field (Optional)
              </label>
              <select
                value={stackField}
                onChange={(e) => setStackField(e.target.value)}
                className="w-full border rounded px-3 py-2"
              >
                <option value="">None</option>
                {categoryFields.map(field => (
                  <option key={field.value} value={field.value}>
                    {field.label}
                  </option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-1">
                Field to create stacked segments within each bar
              </p>
            </div>
          )}

          {/* Pivot Table Options */}
          {chartType === 'pivot' && (
            <>
              <div>
                <label className="block text-sm font-medium mb-2">
                  Aggregation Type
                </label>
                <select
                  value={aggregationType}
                  onChange={(e) => setAggregationType(e.target.value)}
                  className="w-full border rounded px-3 py-2"
                >
                  {AGGREGATION_TYPES.map(type => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  How to calculate values in the pivot table
                </p>
              </div>

              <div>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={showTotals}
                    onChange={(e) => setShowTotals(e.target.checked)}
                    className="rounded"
                  />
                  <span className="text-sm font-medium">Show row and column totals</span>
                </label>
              </div>
            </>
          )}

          {/* Legend Option */}
          {(chartType === 'bar' || chartType === 'stackedBar' || chartType === 'pie') && (
            <div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={showLegend}
                  onChange={(e) => setShowLegend(e.target.checked)}
                  className="rounded"
                />
                <span className="text-sm font-medium">Show legend</span>
              </label>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-2 pt-4">
            <Button onClick={handleSave} className="flex-1">
              {editingWidget ? 'Update Chart' : 'Add Chart'}
            </Button>
            <Button variant="outline" onClick={onClose} className="flex-1">
              Cancel
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
